import { ArrowStatefulIcon } from '@mm/icons';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { HandlerMethodTypes, LifeCycleThis } from '@mm/utils';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import React, { ReactNode } from 'react';
import Autosuggest from 'react-autosuggest';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { ItemLike } from '../FlatMenu/FlatMenu';
import ProRibbon, { ProRibbonType } from '../ProRibbon/ProRibbon';
import Spinner from '../Spinner';
import './FilterableSelect.scss';

type Children = string | ReactNode | Element[];

type OuterProps = {
  showProBanner?: (entity: ItemLike) => ProRibbonType | undefined;
  className?: string;
  children?: Children;
  renderSuggestionsContainer?: (args: {
    children: Children;
    containerProps: any;
    query: any;
  }) => Children;
  outerValue?: string;
  onChange: (
    id: number | string,
    suggestion?: ItemLike,
    preventChange?: () => void,
  ) => any;
  disabled?: boolean;
  // this component actually expects certain input type for suggestions & does not state it explicitly
  // consumers have to dig into to identify what exactly is expected
  suggestions?: any;
  dynamicChangeSuggestions?: boolean;
  showSuggestOnly?: boolean;
  onBlur?: () => any;
  shouldRenderSuggestions?: () => boolean;
  getShowProBanner?: (suggestion: ItemLike) => any;
  placeholder?: string;
  searchPlaceholder?: string;
  onInput?: (e: React.ChangeEvent<HTMLInputElement>) => any;
  defaultValue?: string;
  name?: string;
  suggestionRenderer?: (suggestion: ItemLike) => ReactNode;
  showSpinner?: boolean;
  spinnerClass?: string;
  spinnerSize?: number;
};

type State = {
  value: string;
  suggestionValue: any;
  suggestionsLocal: any[];
  showSuggestions: boolean;
  suggestion?: ItemLike;
};

type StateHandlers = HandlerMethodTypes<OuterProps, typeof stateHandlers>;

type RefHandlers = {
  onRef: (ref: any) => void;
  handleOutsideClick: (e: any) => void;
  addEnterLogic: () => void;
};

type Handlers = {
  getSuggestions: (value: any) => any[];
  restoreSelector: () => void;
  getSectionSuggestions: (value: any) => any;
  onSuggestionSelected: (e: any, value: any) => void;
  onSuggestionsFetchRequested: (value: any) => void;
  onSuggestionsClearRequested: () => void;
  getSuggestionValue: (suggestion: ItemLike) => string;
  renderSectionTitle: (value: any) => any;
  renderSuggestion: (
    getShowProBanner?: (suggestion: ItemLike) => ProRibbonType | undefined,
  ) => (suggestion: ItemLike) => any;
  onLocalChange: (e: any, value: any) => void;
  addInputLogic: (e: any) => void;
  onInputClick: () => void;
  setSuggestion: (value: ItemLike) => any;
};

type InnerProps = State & StateHandlers & RefHandlers & Handlers;

type Props = OuterProps & InnerProps;

function componentDidMount(this: LifeCycleThis<Props>) {
  const {
    outerValue,
    suggestionValue,
    handleOutsideClick,
    changeSuggestionValue,
  } = this.props;
  document.addEventListener('mousedown', handleOutsideClick, false);
  if (outerValue !== suggestionValue) {
    changeSuggestionValue(outerValue);
  }
}

function componentWillUnmount(this: LifeCycleThis<Props>) {
  document.removeEventListener(
    'mousedown',
    this.props.handleOutsideClick,
    false,
  );
}

function componentWillReceiveProps(
  this: LifeCycleThis<Props>,
  {
    outerValue,
    suggestionValue,
    changeSuggestionValue,
    dynamicChangeSuggestions,
    suggestions,
    value,
    onSuggestionsFetchRequested,
  }: Props,
) {
  if (outerValue !== this.props.outerValue && outerValue !== suggestionValue) {
    changeSuggestionValue(outerValue);
  }
  if (
    dynamicChangeSuggestions &&
    value !== undefined &&
    !isEqual(suggestions, this.props.suggestions)
  ) {
    onSuggestionsFetchRequested({ value });
  }
}

const initialState = ({ value, defaultValue }: any) => ({
  value: defaultValue || '',
  suggestionValue: value,
  suggestionsLocal: [],
  showSuggestions: false,
  suggestion: undefined,
});

const getSectionSuggestions = () => section => section.suggestions;
const onSuggestionSelected =
  ({ changeShowSuggestions, changeSuggestionValue, changeValue, onChange }) =>
  (e, { suggestion }) => {
    let applyChange = true;
    const preventChange = () => {
      applyChange = false;
    };
    onChange(suggestion.id, suggestion, preventChange);

    if (applyChange) {
      changeValue('');
      changeSuggestionValue(suggestion.name);
      changeShowSuggestions(false);
    }
  };

const getSuggestions =
  ({ suggestions }: Props) =>
  value => {
    const inputValue = value.trim().toLowerCase();

    return suggestions.reduce((filtered, item) => {
      // TODO
      if (item.title.toLowerCase().indexOf(inputValue) !== -1) {
        filtered.push(item);
      } else {
        const suggestions = item.suggestions.reduce((filtered, item) => {
          if (item.name.toLowerCase().indexOf(inputValue) !== -1) {
            filtered.push(item);
          }

          return filtered;
        }, []);

        if (suggestions.length !== 0) {
          filtered.push({ title: item.title, suggestions });
        }
      }

      return filtered;
    }, []);
  };

const onSuggestionsFetchRequested =
  ({ changeSuggestions, getSuggestions }) =>
  ({ value }) => {
    changeSuggestions(getSuggestions(value));
  };

const onSuggestionsClearRequested =
  ({ changeSuggestions }) =>
  () => {
    changeSuggestions([]);
  };

const onLocalChange =
  ({ changeValue }) =>
  (e, { newValue }) => {
    changeValue(newValue);
  };

const restoreSelector =
  ({ changeValue, changeShowSuggestions }) =>
  () => {
    changeValue('');
    changeShowSuggestions(false);
  };

const refHandlers = () => {
  let node = null;

  return {
    onRef: () => ref => {
      node = ref;
    },
    handleOutsideClick:
      ({ restoreSelector, addInputLogic, onBlur }) =>
      e => {
        // @ts-ignore
        if (node.contains(e.target)) {
          return;
        }

        restoreSelector();
        if (onBlur) onBlur();
        document.removeEventListener('keydown', addInputLogic);
      },
    addEnterLogic:
      ({ addInputLogic }) =>
      () => {
        document.addEventListener('keydown', addInputLogic);
      },
  };
};

const addInputLogic =
  ({ restoreSelector, changeShowSuggestions, showSuggestions }) =>
  e => {
    if (e.key === ENTER_KEY_NAME) {
      if (showSuggestions) {
        restoreSelector();
      } else if (document.body.contains(e.target)) {
        changeShowSuggestions(true);
      }
    }
  };

const onInputClick =
  ({ toggleShowSuggestions, changeValue, addEnterLogic }) =>
  () => {
    toggleShowSuggestions();
    changeValue('');
    addEnterLogic();
  };

const shouldRenderSuggestions = () => () => true;
const renderSectionTitle = () => section => <strong>{section.title}</strong>;

const renderSuggestion =
  ({ suggestionRenderer }: OuterProps) =>
  getShowProBanner =>
  suggestion => {
    const ribbonType = getShowProBanner
      ? getShowProBanner(suggestion)
      : undefined;

    return (
      <span>
        {ribbonType && (
          <ProRibbon
            ribbonType={ribbonType}
            className="filterable-select__pro-icon"
          />
        )}
        {suggestionRenderer?.(suggestion) || suggestion.name}
      </span>
    );
  };

const getSuggestionValue = () => suggestion => suggestion.name;

const stateHandlers = {
  changeValue: () => value => ({ value }),
  changeSuggestionValue: () => value => ({ suggestionValue: value }),
  changeSuggestions: () => value => ({ suggestionsLocal: value }),
  changeShowSuggestions: () => value => ({ showSuggestions: value }),
  toggleShowSuggestions:
    ({ showSuggestions }: any) =>
    () => ({
      showSuggestions: !showSuggestions,
    }),
  setSuggestion: () => suggestion => ({ suggestion }),
};

const enhance = compose<InnerProps, OuterProps>(
  withStateHandlers<State, StateHandlers, OuterProps>(
    initialState,
    stateHandlers,
  ),
  withHandlers<Props, {}>({ getSuggestions, restoreSelector }),
  withHandlers<Props, {}>({
    getSectionSuggestions,
    onSuggestionSelected,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    shouldRenderSuggestions,
    getSuggestionValue,
    renderSectionTitle,
    renderSuggestion,
    onLocalChange,
    addInputLogic,
  }),
  withHandlers(refHandlers),
  withHandlers({ onInputClick }),
  lifecycle<Props, {}>({
    componentDidMount,
    componentWillUnmount,
    componentWillReceiveProps,
  }),
);

const DEFAULT_SPINNER_CLASS = 'filterable-select__spinner';
const DEFAULT_SPINNER_SIZE = 15;

const FilterableSelectView = (props: Props) => {
  const {
    renderSuggestionsContainer,
    children,
    getSectionSuggestions,
    onSuggestionSelected,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    shouldRenderSuggestions,
    getSuggestionValue,
    renderSectionTitle,
    renderSuggestion,
    suggestionsLocal,
    showSuggestions,
    value = '',
    suggestionValue = '',
    onRef,
    onLocalChange,
    onInput,
    onInputClick,
    disabled,
    className,
    placeholder,
    searchPlaceholder,
    name,
    showSuggestOnly,
    showProBanner,
    showSpinner,
    spinnerClass,
    spinnerSize,
  } = props;

  const inputProps = {
    placeholder: searchPlaceholder || 'Search',
    value,
    ref: ref => {
      if (ref && showSuggestOnly) {
        ref.input.focus();
      }
    },
    onInput,
    onChange: onLocalChange,
    autoFocus: true,
    name,
    type: 'search',
  };

  const classList = classnames('filterable-select', className);
  const inputClass = classnames('filterable-select__input', {
    'filterable-select__input--disabled': disabled,
  });

  const showAutosuggestion = showSuggestOnly || showSuggestions;
  const AutosuggestComponent: any = Autosuggest;

  return (
    <div ref={onRef} className={classList}>
      {children}

      {!showSuggestOnly && (
        <div
          className="filterable-select__input-container"
          onClick={onInputClick}
        >
          <input
            readOnly
            className={inputClass}
            value={suggestionValue}
            placeholder={placeholder}
          />
          <span className="filterable-select__arrow-icon">
            <ArrowStatefulIcon
              direction={showAutosuggestion ? 'up' : 'down'}
              size={10}
              color="#262626"
            />
          </span>
        </div>
      )}

      {showSpinner && (
        <div className="filterable-select__spinner-wrapper">
          <Spinner
            className={spinnerClass || DEFAULT_SPINNER_CLASS}
            show={true}
            position="absolute"
            backgroundColor="none"
            size={spinnerSize || DEFAULT_SPINNER_SIZE}
          />
        </div>
      )}

      {!disabled && showAutosuggestion && (
        <AutosuggestComponent
          renderSuggestionsContainer={renderSuggestionsContainer}
          multiSection
          autoFocus
          ref={ref => {
            if (ref && showSuggestOnly) {
              // @ts-ignore
              ref.input.focus();
            }
          }}
          getSectionSuggestions={getSectionSuggestions}
          suggestions={suggestionsLocal}
          onSuggestionSelected={onSuggestionSelected}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          shouldRenderSuggestions={shouldRenderSuggestions}
          getSuggestionValue={getSuggestionValue}
          renderSectionTitle={renderSectionTitle}
          renderSuggestion={renderSuggestion(showProBanner)}
          inputProps={inputProps}
        />
      )}
    </div>
  );
};

export const FilterableSelect = enhance(FilterableSelectView);

export default FilterableSelect;
